// Data Deletion Instructions page — public, routed at /data-deletion.
//
// Required for Meta App Review (Facebook Login apps must provide a Data
// Deletion Callback URL OR a Data Deletion Instructions URL). This is the
// Instructions URL — it explains how a user removes the data we collected via
// Facebook / Instagram, and all of their account data. Point Meta App
// Settings → "User data deletion" at https://<domain>/data-deletion.
//
// ⚠️ Confirm before launch: the contact email (privacy@adastronaut.com) is live
// (set up email forwarding on hookad.ai → your inbox), and the in-app paths
// below (Account → Delete account; disconnect Facebook) match the shipped UI.

import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const LAST_UPDATED = 'July 23, 2026';
const CONTACT_EMAIL = 'privacy@adastronaut.com';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-xl md:text-2xl font-bold text-dark mb-3">{title}</h2>
      <div className="space-y-3 text-dark-muted text-sm md:text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function DataDeletionPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-dark mb-2">
              Data Deletion
            </h1>
            <p className="text-dark-muted text-sm">Last updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-10">
            <Section id="overview" title="Overview">
              <p>
                You can delete your data from Ad Astronaut at any time. This
                includes the information we collect when you connect a Facebook
                Page or Instagram account, and all of your account data. Choose
                whichever method below is easiest for you.
              </p>
            </Section>

            <Section id="delete-account" title="Delete everything (recommended)">
              <p>
                To permanently delete your account and <strong>all</strong>
                {' '}associated data, sign in and go to{' '}
                <strong>Account &rarr; Delete account</strong> (in the Danger
                Zone at the bottom of the profile page). This immediately and
                permanently removes:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your account and profile</li>
                <li>Your brands, products, and competitor lists</li>
                <li>Every creative you generated and saved</li>
                <li>Any connected Facebook / Instagram tokens and connection records</li>
                <li>Your scheduled posts and their history</li>
              </ul>
              <p>
                This action cannot be undone. Deletion is immediate; backups are
                purged within 30 days.
              </p>
            </Section>

            <Section id="disconnect-facebook" title="Delete only your Facebook / Instagram data">
              <p>
                If you want to keep your Ad Astronaut account but remove the
                connection to Facebook / Instagram, sign in and{' '}
                <strong>disconnect Facebook</strong> from the left sidebar (the
                Facebook and Instagram entries under your connected accounts).
                Disconnecting immediately deletes the access tokens and
                connection records we stored for those accounts, and we stop
                publishing on your behalf.
              </p>
              <p>
                You can also revoke our access directly from Facebook:{' '}
                <strong>
                  Facebook &rarr; Settings &amp; Privacy &rarr; Settings &rarr;
                  Apps and Websites
                </strong>
                , then remove Ad Astronaut. Facebook notifies us, our access
                to your Pages ends, and we delete the associated connection data.
              </p>
            </Section>

            <Section id="email" title="Request deletion by email">
              <p>
                Prefer not to do it yourself? Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                  {CONTACT_EMAIL}
                </a>{' '}
                from the address on your account (or include your account email)
                with the subject <strong>&ldquo;Delete my data&rdquo;</strong>.
                We will verify the request and delete your data within 30 days,
                and confirm by email when it&rsquo;s done.
              </p>
            </Section>

            <Section id="questions" title="Questions">
              <p>
                For anything else about how we handle your information, see our{' '}
                <a href="/privacy" className="text-primary font-semibold hover:underline">
                  Privacy Policy
                </a>{' '}or contact{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary font-semibold hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
